import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useGoogleCalendar } from "@/hooks/useGoogleCalendar";
import { holidaysInRange, formatHolidayLine } from "@/lib/holidays";
import { readShowHolidays } from "@/lib/showHolidaysPref";

const GOOGLE_CAL_URL = "https://unnzlorzyswiahjlxwqh.supabase.co/functions/v1/google-calendar-auth";
const ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InVubnpsb3J6eXN3aWFoamx4d3FoIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzYxMDU5NTMsImV4cCI6MjA5MTY4MTk1M30.4zmLgxfgFlg9mnvkqQVPgsmV8bP1UO_OyLb3wXjgSfo";

/**
 * Auth headers for google-calendar-auth (#494).
 *
 * Was a module-level constant sending `Bearer ${ANON_KEY}` — the publishable
 * key, which ships in this bundle and is public. The Edge Function trusted the
 * user_id in the body while querying with a service-role client, so anyone could
 * read, write to, or disconnect any user's calendar given their UUID.
 *
 * It cannot be a module constant anymore: the token belongs to a session, so it
 * has to be read per call. apikey stays the anon key — that header is Supabase's
 * project router, not an identity claim.
 */
function authHeaders(accessToken: string): Record<string, string> {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`,
    apikey: ANON_KEY,
  };
}

// Session-level flag: once user skips, don't show the connect card again
let sessionSkipped = false;

/**
 * Holidays — the holiday line appended to every "Plan my day/week" message.
 *
 * These two chips are the OTHER place the app builds Kin's schedule context
 * itself (the first is the plan prompt in TodayTab). `kin-chat`'s ambient
 * CALENDAR AWARENESS block is assembled server-side and is deliberately not
 * touched, so without this a mom who taps "Plan my day" on Halloween gets a
 * game plan that has no idea it is Halloween.
 *
 * Appended to ALL FOUR paths below — calendar-connected with events,
 * connected with none, the not-connected fallback, and the error fallback.
 * The no-events paths matter most: "nothing is scheduled today" plus a holiday
 * is exactly the day she wants Kin to notice.
 *
 * ⚠️ Marked as not-her-events for the same reason as the plan prompt: these
 * messages also carry `[TODAY_EVENTS]`, and an unlabelled date line next to
 * real calendar entries reads as one of them.
 */
function holidayNote(mode: "today" | "week"): string {
  if (!readShowHolidays()) return "";
  const today = new Date();
  const start = formatLocalYmd(today);
  const end = mode === "today"
    ? start
    : formatLocalYmd(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 6));
  const rows = holidaysInRange(start, end);
  if (rows.length === 0) return "";
  return (
    `\n[HOLIDAYS]\nUS holidays in this window — NOT my events and NOT to-dos, just what day it is. ` +
    `Don't add them to any list; do mention one warmly if it fits.\n` +
    `${rows.map(formatHolidayLine).join("\n")}\n[/HOLIDAYS]`
  );
}

/** Local YYYY-MM-DD. Never `toISOString`, which shifts the day west of UTC. */
function formatLocalYmd(d: Date): string {
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return `${d.getFullYear()}-${m < 10 ? `0${m}` : m}-${day < 10 ? `0${day}` : day}`;
}

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
}

function formatDayHeader(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
}

function formatEvent(ev: any): string {
  if (ev.all_day || ev.allDay) return `- All day: ${ev.title || ev.summary}`;
  const time = formatTime(ev.start_datetime || ev.start || ev.startDateTime);
  const title = ev.title || ev.summary;
  const location = ev.location;
  return location ? `- ${time}: ${title} at ${location}` : `- ${time}: ${title}`;
}

function groupByDay(events: any[]): string {
  const groups: Record<string, string[]> = {};
  for (const ev of events) {
    const startStr = ev.start_datetime || ev.start || ev.startDateTime;
    const dayKey = formatDayHeader(startStr);
    if (!groups[dayKey]) groups[dayKey] = [];
    groups[dayKey].push(formatEvent(ev));
  }
  return Object.entries(groups)
    .map(([day, lines]) => `${day}:\n${lines.join("\n")}`)
    .join("\n\n");
}

function sendFallback(mode: "today" | "week", onSendMessage: (msg: string, display?: string) => void) {
  if (mode === "today") {
    onSendMessage(
      `Help me plan my morning. I don't have my calendar connected yet so just ask me what I have going on today and build me a game plan from that.${holidayNote("today")}`,
      "Plan my day ☀️"
    );
  } else {
    onSendMessage(
      `Help me plan my week — I'm a working mom with two kids and I need a realistic plan that doesn't make me want to cry. I don't have my calendar connected yet so just ask me what my week looks like.${holidayNote("week")}`,
      "Plan my week 📅"
    );
  }
}

interface PlanPickerProps {
  onSendMessage: (message: string, displayText?: string) => void;
  onDismiss: () => void;
}

const PlanPicker = ({ onSendMessage, onDismiss }: PlanPickerProps) => {
  const { user, session } = useAuth();
  const { connect } = useGoogleCalendar();
  const [loading, setLoading] = useState(false);
  const [showConnectCard, setShowConnectCard] = useState(false);
  const [pendingMode, setPendingMode] = useState<"today" | "week" | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Dismiss on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onDismiss();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onDismiss]);

  const handlePick = async (mode: "today" | "week") => {
    setLoading(true);

    try {
      // Check calendar connection
      let connected = false;
      // Session required now, not just a user — the token is the identity.
      const token = session?.access_token;
      if (user && token) {
        try {
          const resp = await fetch(GOOGLE_CAL_URL, {
            method: "POST",
            headers: authHeaders(token),
            body: JSON.stringify({ action: "check_status", user_id: user.id }),
          });
          if (resp.ok) {
            const data = await resp.json();
            connected = !!data.connected;
          }
        } catch { /* not connected */ }
      }

      if (connected && user && token) {
        // Fetch events
        const action = mode === "today" ? "get_today" : "get_week";
        try {
          const resp = await fetch(GOOGLE_CAL_URL, {
            method: "POST",
            headers: authHeaders(token),
            body: JSON.stringify({ action, user_id: user.id }),
          });
          if (resp.ok) {
            const data = await resp.json();
            const events: any[] = data.events || [];
            if (mode === "today") {
              if (events.length === 0) {
                onSendMessage(`I checked my calendar and nothing is scheduled today. Help me plan my day anyway.${holidayNote("today")}`, "Plan my day ☀️");
              } else {
                const formatted = events.map(formatEvent).join("\n");
                onSendMessage(`[TODAY_EVENTS]\n${formatted}\n[/TODAY_EVENTS]\nPlan my morning and help me get through today. I'm a working mom and I need a realistic game plan.${holidayNote("today")}`, "Plan my day ☀️");
              }
            } else {
              if (events.length === 0) {
                onSendMessage(`I checked my calendar and nothing is scheduled this week. Help me plan my week anyway.${holidayNote("week")}`, "Plan my week 📅");
              } else {
                const formatted = groupByDay(events);
                onSendMessage(`[WEEK_EVENTS]\n${formatted}\n[/WEEK_EVENTS]\nHelp me plan my week. I'm a working mom with two kids and I need a realistic plan that accounts for everything on my calendar. Flag any days that look crazy and help me figure out meals, logistics, and when I'll actually have breathing room.${holidayNote("week")}`, "Plan my week 📅");
              }
            }
          } else {
            sendFallback(mode, onSendMessage);
          }
        } catch {
          sendFallback(mode, onSendMessage);
        }
      } else {
        // Not connected — show connect card or send fallback
        if (sessionSkipped) {
          sendFallback(mode, onSendMessage);
        } else {
          setPendingMode(mode);
          setShowConnectCard(true);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    sessionSkipped = true;
    if (pendingMode) {
      sendFallback(pendingMode, onSendMessage);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-3 animate-fade-in">
        <span className="font-body text-sm" style={{ color: "#5050C8" }}>
          Checking your calendar...
        </span>
      </div>
    );
  }

  if (showConnectCard) {
    return (
      <div
        ref={containerRef}
        className="animate-fade-in w-full"
        style={{
          backgroundColor: "#F0F0FF",
          border: "1px solid #C8C8F4",
          borderRadius: "16px",
          padding: "16px",
          boxShadow: "0 2px 8px rgba(80, 80, 200, 0.08)",
        }}
      >
        <p className="font-display" style={{ fontSize: "15px", color: "#1C1A3E", marginBottom: "6px" }}>
          📅 Connect your calendar for a personalized plan
        </p>
        <p className="font-body" style={{ fontSize: "13px", color: "#64748B", lineHeight: 1.5, marginBottom: "14px" }}>
          Kin can pull your real schedule and build your plan around it. No more listing everything from memory.
        </p>
        <div className="flex gap-3">
          <button
            onClick={connect}
            className="font-body font-medium text-sm cursor-pointer transition-all duration-200 hover:-translate-y-0.5 flex-1"
            style={{
              backgroundColor: "#5050C8",
              color: "#FFFFFF",
              borderRadius: "12px",
              padding: "10px 16px",
              border: "none",
            }}
          >
            Connect Google Calendar
          </button>
          <button
            onClick={handleSkip}
            className="font-body font-medium text-sm cursor-pointer transition-all duration-200 hover:-translate-y-0.5 flex-1"
            style={{
              backgroundColor: "#F0F0FF",
              color: "#1C1A3E",
              borderRadius: "12px",
              padding: "10px 16px",
              border: "1px solid #C8C8F4",
            }}
          >
            Skip for now
          </button>
        </div>
        <p className="font-body" style={{ fontSize: "12px", color: "#54525C", lineHeight: 1.5, marginTop: "12px" }}>
          🍎 Use Apple Calendar? Connect Google Calendar here, then <strong style={{ color: "#1C1A3E" }}>add your Google account in iPhone Settings → Calendar → Accounts</strong>. Your events will show up in both.
        </p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="flex items-center justify-center gap-3 py-2 animate-fade-in">
      <button
        onClick={() => handlePick("today")}
        className="font-body font-medium text-sm cursor-pointer transition-all duration-200 hover:-translate-y-0.5"
        style={{
          backgroundColor: "#5050C8",
          color: "#FFFFFF",
          borderRadius: "20px",
          padding: "10px 24px",
          border: "none",
        }}
      >
        ☀️ Today
      </button>
      <button
        onClick={() => handlePick("week")}
        className="font-body font-medium text-sm cursor-pointer transition-all duration-200 hover:-translate-y-0.5"
        style={{
          backgroundColor: "#F0F0FF",
          color: "#1C1A3E",
          borderRadius: "20px",
          padding: "10px 24px",
          border: "1px solid #C8C8F4",
        }}
      >
        📅 This week
      </button>
    </div>
  );
};

export default PlanPicker;
